import { legacyHttp } from '@noor/api';
import {
  legacyEquipmentListSchema,
  legacyOrderEvalSchema,
  legacySavedLocationListSchema,
  type LegacyEquipment,
  type LegacyOrderDraft,
  type LegacyOrderEval,
  type LegacySavedLocation,
  type LegacyWebviewOrderDraft,
} from '../model/schema';

export const legacyOrderDraftApi = {
  async equipments(): Promise<LegacyEquipment[]> {
    // Nested under an `equipments` key, not returned bare.
    const raw = await legacyHttp.get<unknown>('equipments');
    return legacyEquipmentListSchema.parse(raw).equipments ?? [];
  },

  /**
   * The merchant's saved pickup addresses, optionally narrowed by the typed
   * text — the old panel refires `vendors/locations?is_business=true&search=…`
   * as the origin address is typed (`create/index.tsx:173-184` with
   * `DestinationBox`'s `setFilters`) and lists the matches ABOVE the geocoder
   * suggestions. `shopId` is the webview's handshake filter: the embedding app
   * names the branch whose address should pre-fill the pickup.
   */
  async savedLocations(params?: {
    search?: string;
    shopId?: string;
  }): Promise<LegacySavedLocation[]> {
    const raw = await legacyHttp.get<unknown>('vendors/locations', {
      is_business: true,
      ...(params?.search ? { search: params.search } : {}),
      ...(params?.shopId ? { shop_id: params.shopId } : {}),
    });
    return legacySavedLocationListSchema.parse(raw) ?? [];
  },

  /**
   * Prices a draft WITHOUT creating it. Takes the same body as `create`, so
   * the screen can show a live total as the merchant fills the form.
   */
  async evaluate(draft: LegacyOrderDraft): Promise<LegacyOrderEval> {
    const raw = await legacyHttp.post<unknown>('orders/eval', draft);
    return legacyOrderEvalSchema.parse(raw);
  },

  /**
   * Prices an EXISTING order's amended draft.
   *
   * A different endpoint from `evaluate`, not a variant of it: pricing an edit
   * is relative to the order already placed (the courier may be en route, and
   * the dead-head leg is already partly driven), so the backend needs the id.
   * Sending an edit through `orders/eval` prices it as a brand-new delivery
   * and quotes the merchant a total they will not be charged.
   */
  async evaluateEdit(id: number, draft: LegacyOrderDraft): Promise<LegacyOrderEval> {
    const raw = await legacyHttp.post<unknown>(`orders/${id}/eval`, draft);
    return legacyOrderEvalSchema.parse(raw);
  },

  /** Places the order. Returns the created row; the caller navigates away. */
  create: (draft: LegacyOrderDraft): Promise<unknown> => legacyHttp.post('orders', draft),

  /** The webview's narrower body — same endpoints, different shape. */
  evaluateWebview: async (draft: LegacyWebviewOrderDraft): Promise<LegacyOrderEval> => {
    const raw = await legacyHttp.post<unknown>('orders/eval', draft);
    return legacyOrderEvalSchema.parse(raw);
  },

  createWebview: (draft: LegacyWebviewOrderDraft): Promise<unknown> =>
    legacyHttp.post('orders', draft),

  /**
   * Amends an order in flight. PATCH, and it takes the SAME full body as
   * `create` — this is not a sparse patch: omitting a field is a validation
   * error (`Qiymat bo'sh bola olmaydi`), verified live 2026-08-18.
   */
  update: (id: number, draft: LegacyOrderDraft): Promise<unknown> =>
    legacyHttp.patch(`orders/${id}`, draft),
};
